// src/components/core/EntityEmptyState.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { 
  Add as AddIcon,
  FilterAltOff as ClearFiltersIcon
} from '@mui/icons-material';

import { EmptyState } from '../../design-system/components/core';
import { Button } from '../../design-system/components/core';

// Default entity icons
import {
  AssignmentTurnedIn as TaskIcon,
  Business as ClientIcon,
  BrandingWatermark as BrandIcon,
  Campaign as CampaignIcon,
  AssignmentOutlined as ProjectIcon,
  Article as ContentIcon,
  DescriptionOutlined as DocumentIcon,
  Person as UserIcon,
  Receipt as InvoiceIcon,
  Settings as SettingsIcon,
  List as GenericIcon,
} from '@mui/icons-material';

/**
 * EntityEmptyState - Specialized EmptyState component for entity listings
 * Provides consistent empty states for entity list pages with entity-specific defaults
 * 
 * @param {Object} props - Component props
 * @param {string} props.entityType - Type of entity (task, project, client, etc.)
 * @param {boolean} props.filtered - Whether filters are active
 * @param {Function} props.onClearFilters - Function to clear active filters
 * @param {boolean} props.canCreate - Whether the user can create this entity type
 * @param {Function} props.onCreate - Function to handle entity creation
 * @param {string} props.createLabel - Label for the create button
 * @param {Object} props.customContent - Override default entity content
 * @param {React.ReactNode} props.customIcon - Override default entity icon
 * @param {boolean} props.magical - Whether to apply magical effects
 * @param {Object} props.sx - Additional MUI system props
 * @returns {JSX.Element} Entity-specific empty state component
 */
const EntityEmptyState = ({
  // Entity context
  entityType = 'generic',
  filtered = false,
  onClearFilters,
  
  // Creation action
  canCreate = false,
  onCreate,
  createLabel,
  
  // Content customization
  customContent = {},
  customIcon,
  
  // Style options
  magical = true,
  sx = {},
  
  // Other props passed to EmptyState
  ...rest
}) => {
  // Get entity-specific default content
  const getDefaultContent = () => {
    const entityConfig = {
      task: {
        icon: <TaskIcon fontSize="large" />,
        title: 'No Tasks Yet',
        description: 'Create your first task to get started tracking your work.',
        createLabel: 'Create Task',
        filteredTitle: 'No Matching Tasks',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      project: {
        icon: <ProjectIcon fontSize="large" />,
        title: 'No Projects Yet',
        description: 'Create your first project to organize and manage your work.',
        createLabel: 'Create Project',
        filteredTitle: 'No Matching Projects',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      client: {
        icon: <ClientIcon fontSize="large" />,
        title: 'No Clients Yet',
        description: 'Create your first client to get started managing your client relationships.',
        createLabel: 'Create Client',
        filteredTitle: 'No Matching Clients',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      brand: {
        icon: <BrandIcon fontSize="large" />,
        title: 'No Brands Yet',
        description: 'Create your first brand to manage your brand assets and campaigns.',
        createLabel: 'Create Brand',
        filteredTitle: 'No Matching Brands',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      campaign: {
        icon: <CampaignIcon fontSize="large" />,
        title: 'No Campaigns Yet',
        description: 'Create your first campaign to start planning your marketing initiatives.',
        createLabel: 'Create Campaign',
        filteredTitle: 'No Matching Campaigns',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      content: {
        icon: <ContentIcon fontSize="large" />,
        title: 'No Content Yet',
        description: 'Create your first content piece to start managing your content library.',
        createLabel: 'Create Content',
        filteredTitle: 'No Matching Content',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      document: {
        icon: <DocumentIcon fontSize="large" />,
        title: 'No Documents Yet',
        description: 'Upload your first document to start building your document library.',
        createLabel: 'Upload Document',
        filteredTitle: 'No Matching Documents',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      user: {
        icon: <UserIcon fontSize="large" />,
        title: 'No Users Yet',
        description: 'Add your first user to start building your team.',
        createLabel: 'Add User',
        filteredTitle: 'No Matching Users',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      invoice: {
        icon: <InvoiceIcon fontSize="large" />,
        title: 'No Invoices Yet',
        description: 'Create your first invoice to start tracking your finances.',
        createLabel: 'Create Invoice',
        filteredTitle: 'No Matching Invoices',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
      generic: {
        icon: <GenericIcon fontSize="large" />,
        title: 'No Items Found',
        description: 'There are no items to display at this time.',
        createLabel: 'Create New',
        filteredTitle: 'No Matching Items',
        filteredDescription: 'Try adjusting your filters to find what you\'re looking for.'
      },
    };
    
    // Return config for the entity type, or generic if not found
    return entityConfig[entityType] || entityConfig.generic;
  };
  
  // Get entity default content
  const defaultContent = getDefaultContent();
  
  // Merge default with custom content
  const content = {
    icon: customIcon || defaultContent.icon,
    title: customContent.title || defaultContent.title,
    description: customContent.description || defaultContent.description,
    createLabel: customContent.createLabel || defaultContent.createLabel,
    filteredTitle: customContent.filteredTitle || defaultContent.filteredTitle,
    filteredDescription: customContent.filteredDescription || defaultContent.filteredDescription,
  };
  
  // Handle creation action
  const handleCreate = () => {
    if (onCreate) {
      onCreate();
    }
  };
  
  // Determine appropriate action based on state
  const getAction = () => {
    if (filtered && onClearFilters) {
      return (
        <Button
          variant="outlined"
          startIcon={<ClearFiltersIcon />}
          onClick={onClearFilters}
          magical={false}
        >
          Clear Filters
        </Button>
      );
    }
    
    if (canCreate && onCreate) {
      return (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          magical={magical}
        >
          {createLabel || content.createLabel}
        </Button>
      );
    }
    
    return null;
  };
  
  return (
    <EmptyState
      title={filtered ? content.filteredTitle : content.title}
      description={filtered ? content.filteredDescription : content.description}
      icon={content.icon}
      action={getAction()}
      magical={magical}
      filtered={filtered}
      sx={sx}
      {...rest}
    />
  );
};

EntityEmptyState.propTypes = {
  entityType: PropTypes.oneOf([
    'task', 'project', 'client', 'brand', 'campaign', 'content',
    'document', 'user', 'invoice', 'generic'
  ]),
  filtered: PropTypes.bool,
  onClearFilters: PropTypes.func,
  canCreate: PropTypes.bool,
  onCreate: PropTypes.func,
  createLabel: PropTypes.string,
  customContent: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    createLabel: PropTypes.string,
    filteredTitle: PropTypes.string,
    filteredDescription: PropTypes.string,
  }),
  customIcon: PropTypes.node,
  magical: PropTypes.bool,
  sx: PropTypes.object,
};

export default EntityEmptyState;